import { Component, Input } from '@angular/core';
import { AnalyticsApiService } from '@app/core/api/services/analytics-api.service';
import { formatDate } from '@angular/common';



const abbreviateNumber = (number, decPlaces = 1) => {
  // Determine the power of 10 for rounding
  decPlaces = Math.pow(10, decPlaces);

  // Define the abbreviations
  const abbrev = ['k', 'M', 'B', 'T'];

  // Iterate through abbreviations in reverse order (largest first)
  for (let i = abbrev.length - 1; i >= 0; i--) {
    // Calculate the size corresponding to the abbreviation
    const size = Math.pow(10, (i + 1) * 3);

    // If the number is large enough for the current abbreviation
    if (size <= number) {
      // Scale and round the number
      number = Math.round((number * decPlaces) / size) / decPlaces;

      // Handle cases where rounding up results in the next abbreviation (e.g., 999k becomes 1M)
      if (number === 1000 && i < abbrev.length - 1) {
        number = 1;
        i++; // Move to the next abbreviation
      }

      // Append the abbreviation and break the loop
      return number + abbrev[i];
    }
  }

  // If no abbreviation is needed, return the original number
  return number;
};



let pieChart: any = {
  chart: {
    height: 280,
    type: "donut",
    toolbar: {
      show: true,
      offsetX: 0,
      offsetY: 5,
    },
  },
  plotOptions: {
    pie: {
      expandOnClick: false,
      donut: {
        size: '50%',

      },
    },
  },

  tooltip: {
    enabled: false,
    enabledOnSeries: false
  },

  title: {
    align: 'center',
    margin: 20,
    style: {
      fontSize:  '18px',
    },
  },

  states: {
    hover: {
      filter: {
        type: 'none',
      }
    },
    active: {
      allowMultipleDataPointsSelection: false,
      filter: {
        type: 'none',
      }
    },
  },

  legend: {
    position: 'bottom',
    inverseOrder: true,
    offsetY: 0,
    itemMargin: {
      horizontal: 6,
      vertical: 4
    },
    onItemClick: {
      toggleDataSeries: false
    },
    onItemHover: {
      highlightDataSeries: false
    },
    formatter: function (val: any, opts: any) {
      return val + `: <b>${opts.w.globals.series[opts.seriesIndex]}</b>`;
    },
  },
  dataLabels: {
    enabled: true,
    style: {
      colors: ["#333"],
      fontSize: '12px',
      fontWeight: 'normal',
    },
    background: {
      enabled: true,
      borderWidth: 1,
      opacity: 1,
      padding: 6,
    },
    dropShadow: {
      enabled: false,
    },
  },
  colors: [
    // '#f15b56', red
    '#003151', // blue
    '#19906f', // teal
    '#d39e3e', // gold
  ],
};



let barChart: any = {
  chart: {
    type: "bar",
    height: 175,
    stacked: true,
    toolbar: {
      show: true,
      offsetX: 0,
      offsetY: 0,
    },
    zoom: {
      enabled: false
    }
  },
  plotOptions: {
    bar: {
      horizontal: true,
    }
  },
  tooltip: {
    enabled: false,
    enabledOnSeries: false
  },
  dataLabels: {
    enabled: true,
    style: {
      colors: ["#fff"],
    },
    formatter: function (val: any, opt: any) {
      return '$' + abbreviateNumber(val);
    },
    offsetX: 0,
    dropShadow: {
      enabled: false,
    },
  },
  yaxis: {
    type: 'category',
    categories: ['Donated Amount'],
    labels: {
      show: false
    }
  },
  xaxis: {
    labels: {
      hideOverlappingLabels: true,
      formatter: function(value, timestamp, opts) {
        return '$' + abbreviateNumber(value);
      }
    }
  },
  title: {
    align: 'center',
    offsetY: 20,
    style: {
      fontSize:  '18px',
    },
  },
  legend: {
    itemMargin: {
      horizontal: 6,
      vertical: 4
    },
    position: 'bottom',
    onItemClick: {
      toggleDataSeries: false
    },
    onItemHover: {
      highlightDataSeries: false
    },
    markers: {
      shape: 'circle',
    }
  },
  fill: {
    opacity: 1
  },
  colors: [
    '#003151', // blue
    '#19906f', // teal
  ],
  states: {
    hover: {
      filter: {
        type: 'none',
      }
    },
    active: {
      allowMultipleDataPointsSelection: false,
      filter: {
        type: 'none',
      }
    },
  },
};



let impressionBarChart: any = {
  chart: {
    type: "bar",
    height: 140,
    stacked: true,
    toolbar: {
      show: true,
      offsetX: 0,
      offsetY: 5,
    },
    zoom: {
      enabled: false
    }
  },
  plotOptions: {
    bar: {
      horizontal: true,
    }
  },
  tooltip: {
    enabled: false,
    enabledOnSeries: false
  },
  dataLabels: {
    enabled: false,

  },
  xaxis: {
    // show: false
    labels: {
      hideOverlappingLabels: true,
      formatter: function(value, timestamp, opts) {
        return abbreviateNumber(value);
      }
    }
  },
  yaxis: {
    type: 'category',
    categories: ['Donated Amount'],
    labels: {
      show: false
    }
  },
  title: {
    text: '',
    align: 'right',
    margin: 0,
    offsetX: -25,
    offsetY: 5,
    floating: false,
    style: {
      fontSize:  '15px',
      fontWeight:  'normal',
    },
  },
  fill: {
    opacity: 1
  },
  colors: [
    '#d39e3e', // gold
  ],
  states: {
    hover: {
      filter: {
        type: 'none',
      }
    },
    active: {
      allowMultipleDataPointsSelection: false,
      filter: {
        type: 'none',
      }
    },
  },
};




@Component({
  selector: 'group-analytics',
  templateUrl: './group-analytics.component.html',
  styleUrl: './group-analytics.component.scss'
})
export class GroupAnalyticsComponent {
  public barChart = barChart;
  public impressionBarChart = impressionBarChart;
  public pieChart = pieChart;
  public abbreviateNumber = abbreviateNumber;
  public formatDate = formatDate;


  @Input({transform: (data: any)=> {
    // Regular Form (preserve order of arrays)
    data.impressionLabels = [
      // 'No Response',
      'Responses'
    ];
    data.impressionSeries = [
      {
        name: 'Responses',
        data: [data.responses]
      }
    ];


    // Format Data
    data.donationSeries = [
      {
        name: 'One Time',
        data: [data.oneTimeDonationAmount]
      },
      {
        name: 'Sustained',
        data: [data.sustainDonationAmount]
      }
    ];

    // Update Title
    let responseRate = (data.responses / data.impressions) * 100;
    data.impressionTitle = {
      ...pieChart.title,
      text: `${abbreviateNumber(data.responses)} Conversions`
    };
    data.donationTitle = {
      ...barChart.title,
      text: `${data.totalDonationAmount.toLocaleString('en-US', {style: 'currency', currency: 'USD'})} Donated`
    };

    // Donation Labels
    data.donationLegend = {
      ...barChart.legend,
      formatter: function (val: any, opts: any) {
        return `
          <b>
            ${
              opts.seriesIndex === 0
                ? data.oneTimeDonationCount
                : data.sustainDonationCount
            }
          </b>
          ${val} Donations
        `;
      },
    };

    return data;
  }}) analyticsData: any;
}
